import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import ProtectedRoute from './components/ProtectedRoute';
import AppShell from './components/AppShell';
import { useAuth } from './auth/AuthContext';

import HomePage from './pages/HomePage';
import HomePageChatDesk from './pages/HomePageChatDesk';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ActivatePage from './pages/ActivatePage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AcceptInvitePage from './pages/AcceptInvitePage';
import TermsOfUsePage from './pages/TermsOfUsePage';
import PartnerRegistrationPage from './pages/PartnerRegistrationPage';
import PartnerListPage from './pages/admin/PartnerListPage';
import LcdNcdPage from './pages/admin/LcdNcdPage';

import DenialsListPage from './components/denials/DenialsListPage';
import DenialIntegrationsPage from './components/denials/DenialIntegrationsPage';
import DenialCaseRunPage from './components/denials/DenialCaseRunPage';
import CaseLookupPage from './components/denials/CaseLookupPage';

import IntakeQueuePage from './components/intake/IntakeQueuePage';
import IntakeUploadPage from './components/intake/IntakeUploadPage';
import IntakeCasePage from './components/intake/IntakeCasePage';

import UsersPage from './pages/admin/UsersPage';
import ClientsPage from './pages/admin/ClientsPage';
import RolesPage from './pages/admin/RolesPage';
import AuditPage from './pages/admin/AuditPage';
import AccountPage from './pages/AccountPage';
import BillingDashboard from './pages/billing/BillingDashboard';
import LookupPage from './pages/lookup/LookupPage';
import LookupUploadPage from './pages/lookup/LookupUploadPage';
import LookupCmsPackUploadPage from './pages/lookup/LookupCmsPackUploadPage';

// "The suite" -- top-level product modules at /[Module]. Shared screens are
// reused under multiple routes (Scrubber serves /ClaimRight + /CodingIntelligence;
// PreService serves /MedicalNecessity + /AuthEligibility).
import DenialResolvePage from './pages/suite/DenialResolvePage';
import AppealBuilderPage from './pages/suite/AppealBuilderPage';
import ClaimRightPage from './pages/suite/ClaimRightPage';
import CodingIntelligencePage from './pages/suite/CodingIntelligencePage';
import MedicalNecessityPage from './pages/suite/MedicalNecessityPage';
import AuthEligibilityPage from './pages/suite/AuthEligibilityPage';
import PayerIntelligencePage from './pages/suite/PayerIntelligencePage';

import DashboardPage from './pages/dashboard/DashboardPage';
import CaseBuilderListPage from './pages/case-builder/CaseBuilderListPage';
import CaseBuilderEditorPage from './pages/case-builder/CaseBuilderEditorPage';
import ClaimResolutionPage from './pages/case-builder/ClaimResolutionPage';
import ClaimRunPage from './pages/case-builder/ClaimRunPage';
import ReferenceRulesPage from './pages/case-builder/ReferenceRulesPage';
import ReferenceInstancesPage from './pages/case-builder/ReferenceInstancesPage';
import TestDrivePage from './pages/TestDrivePage';
import ClaimsListPage from './pages/claims/ClaimsListPage';
import ClaimDetailPage from './pages/claims/ClaimDetailPage';
import PayerRosterPage from './pages/payers/PayerRosterPage';
import ContractVariancePage from './pages/analytics/ContractVariancePage';
import DrgHccPage from './pages/analytics/DrgHccPage';
import AskCmPage from './pages/help/AskCmPage';
import UserManualPage from './pages/help/UserManualPage';

function RootRoute() {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : <HomePage />;
}

export default function App() {
  return (
    <Routes>
      {/* Public marketing home (logged-in users go to the app) */}
      <Route path="/" element={<RootRoute />} />

      {/* Alternate marketing home: Denial Chat Desk pivot */}
      <Route path="/chatdesk" element={<HomePageChatDesk />} />

      {/* Public auth routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/activate" element={<ActivatePage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />

      {/* Public legal pages */}
      <Route path="/terms" element={<TermsOfUsePage />} />
      <Route path="/partner" element={<PartnerRegistrationPage />} />

      {/* Authenticated app */}
      <Route
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/case-builder" element={<CaseBuilderListPage />} />
        <Route path="/case-builder/reference-rules" element={<ReferenceRulesPage />} />
        <Route path="/case-builder/reference-instances" element={<ReferenceInstancesPage />} />
        <Route path="/case-builder/:caseId/runs/:executionId" element={<ClaimRunPage />} />
        <Route path="/case-builder/:caseId/resolution" element={<ClaimResolutionPage />} />
        <Route path="/case-builder/:caseId" element={<CaseBuilderEditorPage />} />
        <Route path="/test-drive" element={<TestDrivePage />} />
        <Route path="/claims" element={<ClaimsListPage />} />
        <Route path="/claims/:claimId" element={<ClaimDetailPage />} />
        <Route path="/payers" element={<PayerRosterPage />} />
        <Route path="/analytics/contract-variance" element={<ContractVariancePage />} />
        <Route path="/analytics/drg-hcc" element={<DrgHccPage />} />
        <Route path="/denials" element={<DenialsListPage />} />
        <Route path="/denials/integrations" element={<DenialIntegrationsPage />} />
        <Route path="/intake" element={<IntakeQueuePage />} />
        <Route path="/intake/new" element={<IntakeUploadPage />} />
        <Route path="/intake/:caseId" element={<IntakeCasePage />} />
        <Route path="/denials/:caseId" element={<DenialCaseRunPage />} />
        <Route path="/denials/v4/:caseId" element={<DenialCaseRunPage forceEngine="v4" />} />
        <Route path="/denials/:caseId/lookups" element={<CaseLookupPage />} />

        {/* "The suite" -- 7 product modules at top-level routes. Six pages
            back seven routes (Scrubber and PreService each serve two
            siblings) per the shared-interface grouping. */}
        <Route path="/DenialResolve"      element={<DenialResolvePage />} />
        <Route path="/AppealBuilder"      element={<AppealBuilderPage />} />
        <Route path="/ClaimRight"         element={<ClaimRightPage />} />
        <Route path="/CodingIntelligence" element={<CodingIntelligencePage />} />
        <Route path="/MedicalNecessity"   element={<MedicalNecessityPage />} />
        <Route path="/AuthEligibility"    element={<AuthEligibilityPage />} />
        <Route path="/PayerIntelligence"  element={<PayerIntelligencePage />} />
        <Route path="/admin/users" element={<UsersPage />} />
        <Route path="/admin/clients" element={<ClientsPage />} />
        <Route path="/admin/roles" element={<RolesPage />} />
        <Route path="/admin/audit" element={<AuditPage />} />
        <Route path="/admin/partners" element={<PartnerListPage />} />
        <Route path="/admin/lcd-ncd" element={<LcdNcdPage />} />
        <Route path="/admin/lookup" element={<LookupPage />} />
        <Route path="/admin/lookup/upload" element={<LookupUploadPage />} />
        <Route path="/admin/lookup/upload-cms-pack" element={<LookupCmsPackUploadPage />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/billing" element={<BillingDashboard />} />
        <Route path="/help/chat" element={<AskCmPage />} />
        <Route path="/help/manual" element={<UserManualPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
